// Filter strip that sits above the all-sites map. Hosts:
//   • Searchable Autocomplete for name + address (drills into a site
//     when an option is picked; free-typed text narrows the visible
//     set against name OR address).
//   • Three multi-select chip-inputs — Entity, State, Category —
//     each pulling its options from the data.
//   • "Color by" toggle (icon button group).
//   • Live counter + "Clear filters" link, right-pinned.

import { useMemo } from 'react'
import {
  Autocomplete, Box, Button, InputAdornment,
  Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip,
  Typography, alpha,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import ClearAllRoundedIcon from '@mui/icons-material/ClearAllRounded'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import { motion } from 'framer-motion'

import type { Site } from '@cmms/types'
import { formatSiteName } from '../../../lib/siteDisplayName'
import {
  COLOR_BY_LABELS, colorForValue, type ColorBy,
} from '../lib/siteColors'
import { MultiFilterButton } from './MultiFilterButton'

export interface FilterStripProps {
  /** All sites — used to build the autocomplete + dropdown options. */
  allSites: Site[]
  /** Filtered count (post-filter) — shown next to "Showing X of Y". */
  filteredCount: number
  isDark: boolean

  search: string
  onSearchChange: (q: string) => void
  /** Called when the user picks a specific site from the autocomplete — drills into it. */
  onPickSite: (siteId: string) => void

  entityFilter: string[]
  onEntityFilterChange: (v: string[]) => void
  stateFilter: string[]
  onStateFilterChange: (v: string[]) => void
  categoryFilter: string[]
  onCategoryFilterChange: (v: string[]) => void

  colorBy: ColorBy
  onColorByChange: (v: ColorBy) => void
}

// Shared input styling — chips of equal height that pop against the
// strip's tinted background, with a soft hover/focus state.
const inputBaseSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'background.paper',
    fontSize: 13,
    borderRadius: 2,
    minHeight: 38,
    transition: 'box-shadow 180ms ease, border-color 180ms ease',
    '& fieldset': { borderColor: 'divider', transition: 'border-color 180ms ease' },
    '&:hover fieldset': { borderColor: (t: any) => alpha(t.palette.primary.main, 0.45) },
    '&.Mui-focused': {
      boxShadow: (t: any) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.14)}`,
    },
    '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 },
  },
} as const

export function FilterStrip({
  allSites, filteredCount, isDark,
  search, onSearchChange, onPickSite,
  entityFilter, onEntityFilterChange,
  stateFilter, onStateFilterChange,
  categoryFilter, onCategoryFilterChange,
  colorBy, onColorByChange,
}: FilterStripProps) {
  // Distinct option lists for each dimension, sorted alphabetically.
  const { entities, states, categories } = useMemo(() => {
    const e = new Set<string>(), s = new Set<string>(), c = new Set<string>()
    for (const site of allSites) {
      if (site.entity?.trim())   e.add(site.entity.trim())
      if (site.state?.trim())    s.add(site.state.trim())
      if (site.category?.trim()) c.add(site.category.trim())
    }
    return {
      entities:   Array.from(e).sort((a, b) => a.localeCompare(b)),
      states:     Array.from(s).sort((a, b) => a.localeCompare(b)),
      categories: Array.from(c).sort((a, b) => a.localeCompare(b)),
    }
  }, [allSites])

  const totalSelected =
    entityFilter.length + stateFilter.length + categoryFilter.length
  const isAnyFilterActive =
    search.trim().length > 0 || totalSelected > 0 || colorBy !== 'none'

  function clearAll() {
    onSearchChange('')
    onEntityFilterChange([])
    onStateFilterChange([])
    onCategoryFilterChange([])
    onColorByChange('none')
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        flexShrink: 0,
        px: { xs: 1.5, md: 2 }, py: 1.5,
        borderBottom: '1px solid', borderColor: 'divider',
        // Subtle gradient — feels like a real toolbar surface, not a
        // flat band. Tightly tinted in light mode, near-black in dark.
        background: (t) => t.palette.mode === 'light'
          ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.045)} 0%, ${alpha(t.palette.primary.main, 0.01)} 100%)`
          : `linear-gradient(180deg, ${alpha(t.palette.common.white, 0.04)} 0%, ${alpha(t.palette.common.white, 0.01)} 100%)`,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ rowGap: 1 }}
      >
        {/* Search Autocomplete (name + address) ------------------- */}
        <Autocomplete
          size="small"
          freeSolo
          clearOnBlur={false}
          options={allSites}
          inputValue={search}
          onInputChange={(_, v, reason) => {
            if (reason !== 'reset') onSearchChange(v)
          }}
          onChange={(_, opt) => {
            if (opt && typeof opt !== 'string') onPickSite(opt.id)
          }}
          getOptionLabel={(opt) => typeof opt === 'string' ? opt : formatSiteName(opt)}
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.trim().toLowerCase()
            if (!q) return opts.slice(0, 25)
            const matches = opts.filter((s) => {
              const hay = [s.name, s.address].filter(Boolean).join(' ').toLowerCase()
              return hay.includes(q)
            })
            return matches.slice(0, 25)
          }}
          renderOption={(props, opt) => {
            const { key, ...rest } = props as any
            return (
              <li key={key} {...rest} style={{ padding: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}
                  sx={{ width: '100%', px: 1.25, py: 0.75 }}>
                  <PlaceOutlinedIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatSiteName(opt)}
                    </Typography>
                    {opt.address && (
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {opt.address}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </li>
            )
          }}
          renderInput={(p) => (
            <TextField {...p}
              placeholder="Find a site by name or address…"
              sx={{ ...inputBaseSx, minWidth: 260 }}
              InputProps={{
                ...p.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          sx={{ flex: '1 1 260px', minWidth: 240, maxWidth: 360 }}
        />

        {/* Multi-select filters — popover-based buttons (clean, no
            inline-chip overflow). ----------------------------------- */}
        <MultiFilterButton
          icon={<DomainOutlinedIcon sx={{ fontSize: 16 }} />}
          label="Entity"
          options={entities}
          value={entityFilter}
          onChange={onEntityFilterChange}
          accent={colorBy === 'entity'}
          swatchForValue={(v) =>
            colorBy === 'entity' ? colorForValue(v, isDark) : undefined}
        />
        <MultiFilterButton
          icon={<PublicOutlinedIcon sx={{ fontSize: 16 }} />}
          label="State"
          options={states}
          value={stateFilter}
          onChange={onStateFilterChange}
          accent={colorBy === 'state'}
          swatchForValue={(v) =>
            colorBy === 'state' ? colorForValue(v, isDark) : undefined}
        />
        <MultiFilterButton
          icon={<CategoryOutlinedIcon sx={{ fontSize: 16 }} />}
          label="Category"
          options={categories}
          value={categoryFilter}
          onChange={onCategoryFilterChange}
          accent={colorBy === 'category'}
          swatchForValue={(v) =>
            colorBy === 'category' ? colorForValue(v, isDark) : undefined}
        />

        {/* Right-pinned cluster: color-by + counter + clear ------- */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }} />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ rowGap: 1 }}
          flexWrap="wrap" useFlexGap>
          {/* Color-by — icon toggle group, much more visual than a select. */}
          <Tooltip title="Pick a dimension to colour-code the map" arrow>
            <Stack direction="row" alignItems="center" spacing={0.75}
              sx={{
                px: 1, py: 0.4,
                bgcolor: 'background.paper',
                border: '1px solid', borderColor: 'divider',
                borderRadius: 2,
                height: 38,
              }}>
              <PaletteOutlinedIcon sx={{ fontSize: 16,
                color: colorBy === 'none' ? 'text.disabled' : 'primary.main' }} />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={colorBy}
                onChange={(_, v) => v && onColorByChange(v as ColorBy)}
                sx={{
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    px: 1.25, py: 0.25,
                    fontSize: 11.5, fontWeight: 700,
                    textTransform: 'none',
                    color: 'text.secondary',
                    borderRadius: '6px !important',
                    minWidth: 0,
                    '&.Mui-selected': {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
                      },
                    },
                    '&:hover': {
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
                    },
                  },
                }}
              >
                {(['none', 'entity', 'state', 'category'] as ColorBy[]).map((opt) => (
                  <ToggleButton key={opt} value={opt}>
                    {opt === 'none' ? <BlockRoundedIcon sx={{ fontSize: 13 }} /> : COLOR_BY_LABELS[opt]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          </Tooltip>

          {/* Count pill ----------------------------------------- */}
          <Tooltip title={`${filteredCount} of ${allSites.length} sites match filters`} arrow>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 1.5,
              bgcolor: 'background.paper',
              border: '1px solid', borderColor: 'divider',
              borderRadius: 2,
              height: 38, boxSizing: 'border-box',
            }}>
              <Typography component="span" sx={{
                fontSize: 14, fontWeight: 800, color: 'text.primary',
                lineHeight: 1,
              }}>
                {filteredCount}
              </Typography>
              <Typography component="span" sx={{
                fontSize: 11.5, color: 'text.disabled', fontWeight: 600,
                lineHeight: 1,
              }}>
                / {allSites.length} sites
              </Typography>
            </Box>
          </Tooltip>

          {/* Clear filters — only shows when something is set ----- */}
          {isAnyFilterActive && (
            <Button
              component={motion.button}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              size="small"
              variant="text"
              startIcon={<ClearAllRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={clearAll}
              sx={{
                fontSize: 12.5, fontWeight: 700,
                color: 'text.secondary', textTransform: 'none',
                height: 38, borderRadius: 2, px: 1.25,
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                },
              }}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}

